import { Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { NgbDropdownModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateCompiler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MESSAGE_FORMAT_CONFIG, TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { HttpClient, HttpHeaders, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import { BackendService, BaseDataService, ConfigService, HandleMissingLabel, maintenanceInterceptor, UserService } from 'nr-common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ComponentsModule } from './components/components.module';
import { ApiService } from './services/api.service';
import { BannerService } from './services/banner.service';
import { DestinationService } from './services/destination.service';
import { IdeaService } from './services/idea.service';
import { TripService } from './services/trip.service';
import { WebsiteService } from './services/website.service';
import { LOCATION_INITIALIZED, registerLocaleData } from '@angular/common';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { deLocale, enGbLocale, esLocale, frLocale, itLocale, nlBeLocale } from 'ngx-bootstrap/locale';
import localeIt from '@angular/common/locales/it';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeNl from '@angular/common/locales/nl';
import moment from 'moment';
import { NrMapsModule } from 'nr-maps';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

function userFactory(
	config: ConfigService,
	user: UserService,
	website: WebsiteService,
	router: Router,
) {
	registerLocaleData(localeIt, 'it');
	registerLocaleData(localeFr, 'fr');
	registerLocaleData(localeEn, 'en');
	registerLocaleData(localeDe, 'de');
	registerLocaleData(localeEs, 'es');
	registerLocaleData(localeNl, 'nl');
	defineLocale('it', itLocale);
	defineLocale('de', deLocale);
	defineLocale('es', esLocale);
	defineLocale('fr', frLocale);
	defineLocale('en', enGbLocale);
	defineLocale('nl', nlBeLocale);

	moment.locale('it');

	config.from(environment);

	if (config.isMaintenanceActive) {
		website.initOffline();
		return () => Promise.resolve();
	}
	const p1 = user.init();
	const p2 = website.init();
	return () => Promise.all([p1, p2]);
}

//#region Translation customization
//? Aggiunta per risolvere gli errori con il translate.instant
function appInitializerFactory(translate: TranslateService, injector: Injector) {
	return async () => {
		await injector.get(LOCATION_INITIALIZED, Promise.resolve(null));

		const langToSet = translate.currentLang;
		try {
			await translate.use(langToSet).toPromise();
		} catch (err) {
			console.error(`Problem with '${langToSet}' language initialization: ${err}`);
		}
	};
}

export class CustomTranslateLoader implements TranslateLoader  {
	public contentHeader = new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*' });

	public constructor(private http: HttpClient) {}

	public getTranslation(langCode: string): Observable<any> {
		var apiAddress = `${environment.servicesEndpointRoot}${environment.i18nFiles}b2c/${langCode}`;
		return this.http.post(apiAddress, {}, { headers: this.contentHeader, withCredentials: true });
	}
}

export class CustomMissingTranslationHandler implements MissingTranslationHandler {
	private missingKeys = new Set<string>();

	public constructor(
		private backend: BackendService,
	) {
	}

	handle(params: MissingTranslationHandlerParams) {
		const key = params.key;
		if (this.missingKeys.has(key)) {
			return key;
		}

		this.missingKeys.add(key);

		HandleMissingLabel('b2c', params, this.backend);

		return key;
	}
}
//#endregion Translation customization

@NgModule({ declarations: [AppComponent],
	bootstrap: [AppComponent], imports: [BrowserModule,
		AppRoutingModule,
		NgbModule,
		NgbDropdownModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useClass: CustomTranslateLoader,
				deps: [HttpClient],
			},
			compiler: {
				provide: TranslateCompiler,
				useClass: TranslateMessageFormatCompiler,
			},
			missingTranslationHandler: {
				provide: MissingTranslationHandler,
				useClass: CustomMissingTranslationHandler,
				deps: [BackendService],
			},
		}),
		BrowserAnimationsModule,
		NrMapsModule.forRoot({
			apiKey: environment.googleMapsApiKey,
		}),
		ComponentsModule], providers: [
		ApiService,
		BannerService,
		BaseDataService,
		DestinationService,
		IdeaService,
		Title,
		WebsiteService,
		//WidgetService,
		TripService,
		provideAppInitializer(() => {
			const initializerFn = (userFactory)(inject(ConfigService), inject(UserService), inject(WebsiteService), inject(Router));
			return initializerFn();
		}),
		provideAppInitializer(() => {
			const initializerFn = (appInitializerFactory)(inject(TranslateService), inject(Injector));
			return initializerFn();
		}),
		{
			provide: MESSAGE_FORMAT_CONFIG,
			useValue: { locales: ['it', 'en', 'fr', 'de', 'es', 'nl'] },
		},
		provideHttpClient(
			withInterceptors([maintenanceInterceptor]),
		),
	] })
export class AppModule {
}

/**
	https://github.com/ngx-translate/core#installation
	https://www.codeandweb.com/babeledit/tutorials/how-to-translate-your-angular-app-with-ngx-translate
	https://www.codeandweb.com/babeledit	*** Editor che tiene sincronizzati i file multilingua
*/
